/**
 * Backend Error Message Translation Keys
 *
 * This file contains translation keys for error messages returned by the backend
 * inventory modules (mod-inventory and mod-inventory-storage).
 *
 * These keys are used to translate backend error messages in the ErrorDetailsModal
 * and other error handling components.
 */

// Instance-related error messages
export const INSTANCE_ERROR_KEYS = {
  // General instance errors
  'Instance not found':
    'ui-plugin-inventory-templates.errors.instance.notFound',
  'Not found': 'ui-plugin-inventory-templates.errors.instance.notFound',
  'HRID can not be updated':
    'ui-plugin-inventory-templates.errors.instance.hridCannotUpdate',
  'The hrid field cannot be changed':
    'ui-plugin-inventory-templates.errors.instance.hridCannotChange',

  // Instance validation errors
  'Bad request': 'ui-plugin-inventory-templates.errors.instance.badRequest',
  'Validation exception':
    'ui-plugin-inventory-templates.errors.instance.validationException',
  'Expected CQL but query parameter is empty':
    'ui-plugin-inventory-templates.errors.instance.emptyCqlQuery',

  // Instance creation/update errors
  'Instance creation failed':
    'ui-plugin-inventory-templates.errors.instance.creationFailed',
  'Instance update failed':
    'ui-plugin-inventory-templates.errors.instance.updateFailed',
  'Instance deletion failed':
    'ui-plugin-inventory-templates.errors.instance.deletionFailed',

  // Instance relationship errors
  'Instance relationship not found':
    'ui-plugin-inventory-templates.errors.instance.relationshipNotFound',
  'Instance relationship creation failed':
    'ui-plugin-inventory-templates.errors.instance.relationshipCreationFailed',

  // Instance source record errors
  'Source record not found':
    'ui-plugin-inventory-templates.errors.instance.sourceRecordNotFound',
  'MARC record not found':
    'ui-plugin-inventory-templates.errors.instance.marcRecordNotFound',
  'Not implemented yet':
    'ui-plugin-inventory-templates.errors.instance.notImplemented',
};

// Holdings-related error messages
export const HOLDINGS_ERROR_KEYS = {
  // General holdings errors
  'Holdings not found':
    'ui-plugin-inventory-templates.errors.holdings.notFound',
  'Holding not found': 'ui-plugin-inventory-templates.errors.holdings.notFound',
  'Not found': 'ui-plugin-inventory-templates.errors.holdings.notFound',
  'HRID can not be updated':
    'ui-plugin-inventory-templates.errors.holdings.hridCannotUpdate',
  'The hrid field cannot be changed':
    'ui-plugin-inventory-templates.errors.holdings.hridCannotChange',

  // Holdings validation errors
  'Bad request': 'ui-plugin-inventory-templates.errors.holdings.badRequest',
  'Validation exception':
    'ui-plugin-inventory-templates.errors.holdings.validationException',
  'Expected CQL but query parameter is empty':
    'ui-plugin-inventory-templates.errors.holdings.emptyCqlQuery',

  // Holdings creation/update errors
  'Holdings creation failed':
    'ui-plugin-inventory-templates.errors.holdings.creationFailed',
  'Holdings update failed':
    'ui-plugin-inventory-templates.errors.holdings.updateFailed',
  'Holdings deletion failed':
    'ui-plugin-inventory-templates.errors.holdings.deletionFailed',

  // Holdings location errors
  'Location not found':
    'ui-plugin-inventory-templates.errors.holdings.locationNotFound',
  'Invalid location':
    'ui-plugin-inventory-templates.errors.holdings.invalidLocation',
  'Permanent location is required':
    'ui-plugin-inventory-templates.errors.holdings.permanentLocationRequired',

  // Holdings call number errors
  'Invalid call number':
    'ui-plugin-inventory-templates.errors.holdings.invalidCallNumber',
  'Call number type not found':
    'ui-plugin-inventory-templates.errors.holdings.callNumberTypeNotFound',

  // Holdings instance relationship errors
  'Instance not found for holdings':
    'ui-plugin-inventory-templates.errors.holdings.instanceNotFound',
  'holdingsRecordId not found':
    'ui-plugin-inventory-templates.errors.holdings.holdingsRecordIdNotFound',
};

// Items-related error messages
export const ITEMS_ERROR_KEYS = {
  // General item errors
  'No item found': 'ui-plugin-inventory-templates.errors.items.notFound',
  'Item not found': 'ui-plugin-inventory-templates.errors.items.notFound',
  'Not found': 'ui-plugin-inventory-templates.errors.items.notFound',
  'Record not Found':
    'ui-plugin-inventory-templates.errors.items.recordNotFound',
  'HRID can not be updated':
    'ui-plugin-inventory-templates.errors.items.hridCannotUpdate',
  'The hrid field cannot be changed':
    'ui-plugin-inventory-templates.errors.items.hridCannotChange',

  // Item validation errors
  'Bad request': 'ui-plugin-inventory-templates.errors.items.badRequest',
  'Validation exception':
    'ui-plugin-inventory-templates.errors.items.validationException',
  'Expected CQL but query parameter is empty':
    'ui-plugin-inventory-templates.errors.items.emptyCqlQuery',

  // Item creation/update errors
  'Item creation failed':
    'ui-plugin-inventory-templates.errors.items.creationFailed',
  'Item update failed':
    'ui-plugin-inventory-templates.errors.items.updateFailed',
  'Item deletion failed':
    'ui-plugin-inventory-templates.errors.items.deletionFailed',

  // Item status errors
  'Claimed returned item cannot be marked as missing':
    'ui-plugin-inventory-templates.errors.items.claimedReturnedCannotBeMissing',
  'Invalid item status':
    'ui-plugin-inventory-templates.errors.items.invalidStatus',
  'Item status update failed':
    'ui-plugin-inventory-templates.errors.items.statusUpdateFailed',

  // Item barcode errors
  'Barcode already exists':
    'ui-plugin-inventory-templates.errors.items.barcodeAlreadyExists',
  'Invalid barcode':
    'ui-plugin-inventory-templates.errors.items.invalidBarcode',
  'Barcode is required':
    'ui-plugin-inventory-templates.errors.items.barcodeRequired',
  'already exists in table item':
    'ui-plugin-inventory-templates.errors.items.barcodeAlreadyExists',
  'Barcode must be unique, %s is already assigned to another item':
    'ui-plugin-inventory-templates.errors.items.barcodeUniqueViolation',

  // Item holdings relationship errors
  'Holdings record not found':
    'ui-plugin-inventory-templates.errors.items.holdingsRecordNotFound',
  'holdingsRecordId not found':
    'ui-plugin-inventory-templates.errors.items.holdingsRecordIdNotFound',
  'Cannot set item':
    'ui-plugin-inventory-templates.errors.items.cannotSetItemField',

  // Item material type errors
  'Material type not found':
    'ui-plugin-inventory-templates.errors.items.materialTypeNotFound',
  'Invalid material type':
    'ui-plugin-inventory-templates.errors.items.invalidMaterialType',

  // Item loan type errors
  'Loan type not found':
    'ui-plugin-inventory-templates.errors.items.loanTypeNotFound',
  'Invalid loan type':
    'ui-plugin-inventory-templates.errors.items.invalidLoanType',

  // Item location errors
  'Location not found':
    'ui-plugin-inventory-templates.errors.items.locationNotFound',
  'Invalid location':
    'ui-plugin-inventory-templates.errors.items.invalidLocation',
};

// Database and system errors
export const SYSTEM_ERROR_KEYS = {
  // Database errors
  'Internal server error':
    'ui-plugin-inventory-templates.errors.system.internalServerError',
  'Database connection error':
    'ui-plugin-inventory-templates.errors.system.databaseConnectionError',
  'Transaction failed':
    'ui-plugin-inventory-templates.errors.system.transactionFailed',
  'Unable to commit transaction':
    'ui-plugin-inventory-templates.errors.system.commitTransactionFailed',
  'Unable to revert transaction':
    'ui-plugin-inventory-templates.errors.system.revertTransactionFailed',

  // Constraint violations
  'Foreign key violation':
    'ui-plugin-inventory-templates.errors.system.foreignKeyViolation',
  'Unique constraint violation':
    'ui-plugin-inventory-templates.errors.system.uniqueConstraintViolation',
  'Check constraint violation':
    'ui-plugin-inventory-templates.errors.system.checkConstraintViolation',

  // Version conflicts
  'Version conflict':
    'ui-plugin-inventory-templates.errors.system.versionConflict',
  'Optimistic locking failed':
    'ui-plugin-inventory-templates.errors.system.optimisticLockingFailed',

  // Network and timeout errors
  'Request timeout':
    'ui-plugin-inventory-templates.errors.system.requestTimeout',
  'Network error': 'ui-plugin-inventory-templates.errors.system.networkError',
  'Service unavailable':
    'ui-plugin-inventory-templates.errors.system.serviceUnavailable',

  // Permission errors
  'Access denied': 'ui-plugin-inventory-templates.errors.system.accessDenied',
  'Insufficient permissions':
    'ui-plugin-inventory-templates.errors.system.insufficientPermissions',
  'Authentication required':
    'ui-plugin-inventory-templates.errors.system.authenticationRequired',

  // Database specific errors
  'RAISE statement option cannot be null':
    'ui-plugin-inventory-templates.errors.system.raiseStatementNull',
  'ERROR: RAISE statement option cannot be null (22004)':
    'ui-plugin-inventory-templates.errors.system.raiseStatementNull',
};

// Validation-specific error messages
export const VALIDATION_ERROR_KEYS = {
  // Required field errors
  'Field is required':
    'ui-plugin-inventory-templates.errors.validation.fieldRequired',
  'Title is required':
    'ui-plugin-inventory-templates.errors.validation.titleRequired',
  'Instance type is required':
    'ui-plugin-inventory-templates.errors.validation.instanceTypeRequired',
  'Material type is required':
    'ui-plugin-inventory-templates.errors.validation.materialTypeRequired',
  'Loan type is required':
    'ui-plugin-inventory-templates.errors.validation.loanTypeRequired',
  'Location is required':
    'ui-plugin-inventory-templates.errors.validation.locationRequired',

  // Format validation errors
  'Invalid format':
    'ui-plugin-inventory-templates.errors.validation.invalidFormat',
  'Invalid date format':
    'ui-plugin-inventory-templates.errors.validation.invalidDateFormat',
  'Invalid UUID format':
    'ui-plugin-inventory-templates.errors.validation.invalidUuidFormat',
  'Invalid JSON format':
    'ui-plugin-inventory-templates.errors.validation.invalidJsonFormat',

  // Length validation errors
  'Field too long':
    'ui-plugin-inventory-templates.errors.validation.fieldTooLong',
  'Field too short':
    'ui-plugin-inventory-templates.errors.validation.fieldTooShort',
  'Note too long':
    'ui-plugin-inventory-templates.errors.validation.noteTooLong',

  // Value validation errors
  'Invalid value':
    'ui-plugin-inventory-templates.errors.validation.invalidValue',
  'Value out of range':
    'ui-plugin-inventory-templates.errors.validation.valueOutOfRange',
  'Duplicate value':
    'ui-plugin-inventory-templates.errors.validation.duplicateValue',
};

// Combine all error keys for easy lookup
export const ALL_ERROR_KEYS = {
  ...INSTANCE_ERROR_KEYS,
  ...HOLDINGS_ERROR_KEYS,
  ...ITEMS_ERROR_KEYS,
  ...SYSTEM_ERROR_KEYS,
  ...VALIDATION_ERROR_KEYS,
};

/**
 * Extract barcode from error message
 * @param {string} errorMessage - The error message containing barcode
 * @returns {string|null} - The extracted barcode or null
 */
export const extractBarcodeFromError = (errorMessage) => {
  // Pattern: "Barcode must be unique, 100023 is already assigned to another item"
  const barcodeMatch = errorMessage.match(
    /Barcode must be unique, ([^\s]+) is already assigned/,
  );
  return barcodeMatch ? barcodeMatch[1] : null;
};

/**
 * Function to get translation key for a backend error message
 * @param {string} errorMessage - The error message from backend
 * @param {string} category - The category of the error (instance, holdings, items, system, validation)
 * @returns {Object} - Object containing translation key and extracted parameters
 */
export const getErrorTranslationKey = (errorMessage, category = 'system') => {
  if (!errorMessage) {
    return {
      key: 'ui-plugin-inventory-templates.errors.unknown',
      params: {},
    };
  }

  // Check for barcode uniqueness error first
  if (
    errorMessage.includes('Barcode must be unique') &&
    errorMessage.includes('is already assigned')
  ) {
    const barcode = extractBarcodeFromError(errorMessage);
    return {
      key: 'ui-plugin-inventory-templates.errors.items.barcodeUniqueViolation',
      params: { barcode: barcode || 'unknown' },
    };
  }

  // Direct lookup
  if (ALL_ERROR_KEYS[errorMessage]) {
    return {
      key: ALL_ERROR_KEYS[errorMessage],
      params: {},
    };
  }

  // Pattern matching for common error patterns
  const message = errorMessage.toLowerCase();

  // HRID errors
  if (message.includes('hrid') && message.includes('cannot')) {
    return {
      key: `ui-plugin-inventory-templates.errors.${category}.hridCannotUpdate`,
      params: {},
    };
  }

  // Not found errors
  if (message.includes('not found')) {
    return {
      key: `ui-plugin-inventory-templates.errors.${category}.notFound`,
      params: {},
    };
  }

  // Already exists errors
  if (
    message.includes('already exists') ||
    message.includes('already assigned')
  ) {
    if (message.includes('barcode')) {
      return {
        key: 'ui-plugin-inventory-templates.errors.items.barcodeAlreadyExists',
        params: {},
      };
    }
    return {
      key: `ui-plugin-inventory-templates.errors.${category}.alreadyExists`,
      params: {},
    };
  }

  // Barcode uniqueness errors
  if (message.includes('barcode must be unique')) {
    return {
      key: 'ui-plugin-inventory-templates.errors.items.barcodeAlreadyExists',
      params: {},
    };
  }

  // Required field errors
  if (message.includes('required') || message.includes('is required')) {
    return {
      key: 'ui-plugin-inventory-templates.errors.validation.fieldRequired',
      params: {},
    };
  }

  // Bad request errors
  if (message.includes('bad request')) {
    return {
      key: `ui-plugin-inventory-templates.errors.${category}.badRequest`,
      params: {},
    };
  }

  // Validation errors
  if (message.includes('validation')) {
    return {
      key: `ui-plugin-inventory-templates.errors.${category}.validationException`,
      params: {},
    };
  }

  // Internal server errors
  if (message.includes('internal server error') || message.includes('500')) {
    return {
      key: 'ui-plugin-inventory-templates.errors.system.internalServerError',
      params: {},
    };
  }

  // Fallback to generic error
  return {
    key: 'ui-plugin-inventory-templates.errors.unknown',
    params: {},
  };
};

/**
 * Shared utility function for translating error messages
 * @param {Object} error - The error object
 * @param {Object} intl - React Intl object
 * @returns {string} - Translated error message
 */
export const getTranslatedErrorMessage = (error, intl) => {
  const translationResult = getErrorTranslationKey(
    error.message,
    error.category,
  );
  try {
    return intl.formatMessage(
      { id: translationResult.key },
      {
        ...translationResult.params,
        defaultMessage: error.message,
      },
    );
  } catch (e) {
    return error.displayMessage || error.message;
  }
};

/**
 * Function to extract error details from backend response
 * @param {Object} error - The error object from backend
 * @returns {Object} - Extracted error details
 */
export const extractBackendErrorDetails = (error) => {
  let message = 'Unknown error';
  let code = 'UNKNOWN';
  let category = 'system';

  if (error.response) {
    // HTTP response error
    code = error.response.status || 'UNKNOWN';

    if (error.response.data) {
      if (typeof error.response.data === 'string') {
        message = error.response.data;
      } else if (
        error.response.data.errors &&
        error.response.data.errors.length > 0
      ) {
        // FOLIO API error format
        const firstError = error.response.data.errors[0];
        message = firstError.message || firstError.code || 'Validation error';
        code = firstError.code || code;
      } else if (error.response.data.error) {
        message = error.response.data.error;
      } else if (error.response.data.message) {
        message = error.response.data.message;
      }
    } else {
      message = error.response.statusText || `HTTP ${code}`;
    }
  } else if (error.message) {
    message = error.message;
  }

  // Determine category based on message content
  if (message.toLowerCase().includes('instance')) {
    category = 'instance';
  } else if (message.toLowerCase().includes('holding')) {
    category = 'holdings';
  } else if (message.toLowerCase().includes('item')) {
    category = 'items';
  } else if (message.toLowerCase().includes('validation')) {
    category = 'validation';
  }

  const translationResult = getErrorTranslationKey(message, category);

  return {
    message,
    code,
    category,
    translationKey: translationResult.key,
    translationParams: translationResult.params,
  };
};
